// src/paths.ts
import * as z from "zod";
import { DEFAULT_EQUIPMENT_SPACE_ID, RECALLS_STATE_ID, recallVerdictId } from "@rock/types";
import { cleanFirestoreId } from "@rock/util";
var FirestoreDocPathSchema = z.string().brand();
var DRAFT_ROOT = "_draft/db";
var DRAFT_SEGMENT_PREFIX = "draft:";
var toDraftPath = (path) => `${DRAFT_ROOT}/${path.split("/").map((seg, i) => i % 2 === 0 ? `${DRAFT_SEGMENT_PREFIX}${seg}` : seg).join("/")}`;
var fromDraftPath = (draftPath) => {
  const rootPrefix = `${DRAFT_ROOT}/`;
  if (!draftPath.startsWith(rootPrefix)) throw new Error(`Not a draft path: ${draftPath}`);
  return draftPath.slice(rootPrefix.length).split("/").map((seg, i) => {
    if (i % 2 !== 0) return seg;
    if (!seg.startsWith(DRAFT_SEGMENT_PREFIX)) throw new Error(`Not a draft path: ${draftPath}`);
    return seg.slice(DRAFT_SEGMENT_PREFIX.length);
  }).join("/");
};
var appCollection = () => "app";
var appEquipmentConfigPath = () => `${appCollection()}/equipment-config`;
var appTaskScreenConfigPath = () => `${appCollection()}/task-screen-config`;
var appUserLimitsConfigPath = () => `${appCollection()}/user-limits-config`;
var appBrandSearchIndexPath = () => `${appCollection()}/brand-search-index`;
var appEquipmentConfigObjectsCollection = () => `${appEquipmentConfigPath()}/objects`;
var appDefaultEquipmentGuidePath = () => `${appEquipmentConfigObjectsCollection()}/guide`;
var appStatusCollection = () => "app-status";
var appStatusDocPath = (kind) => `${appStatusCollection()}/${kind}`;
var systemCollection = () => "system";
var resetStatePath = () => `${systemCollection()}/reset-state`;
var feedbackCollection = () => "feedback";
var feedbackEntryPath = (feedbackId) => `${feedbackCollection()}/${cleanFirestoreId(feedbackId)}`;
var spacesCollection = () => "spaces";
var spacePath = (spaceId) => `${spacesCollection()}/${cleanFirestoreId(spaceId)}`;
var spaceObjectsCollection = (spaceId) => `${spacePath(spaceId)}/objects`;
var guidePath = (context) => context.type === "space" ? context.spaceId === DEFAULT_EQUIPMENT_SPACE_ID ? appDefaultEquipmentGuidePath() : `${spaceObjectsCollection(context.spaceId)}/guide` : `${equipmentTypeObjectsCollection(context.typeId)}/guide`;
var guideVisitsCollection = () => "guide-visits";
var guideVisitPath = (guideVisitId) => `${guideVisitsCollection()}/${cleanFirestoreId(guideVisitId)}`;
var equipmentTypesCollection = () => "equipment-types";
var equipmentTypePath = (typeId) => `${equipmentTypesCollection()}/${cleanFirestoreId(typeId)}`;
var equipmentTypeFamiliesCollection = () => "equipment-type-families";
var equipmentTypeFamilyPath = (familyId) => `${equipmentTypeFamiliesCollection()}/${cleanFirestoreId(familyId)}`;
var infoTypesCollection = () => "info-types";
var infoTypePath = (typeId) => `${infoTypesCollection()}/${cleanFirestoreId(typeId)}`;
var equipmentTestLibraryCollection = () => "equipment-test-library";
var equipmentTestLibraryEntryPath = (entryId) => `${equipmentTestLibraryCollection()}/${cleanFirestoreId(entryId)}`;
var equipmentTypeCandidatesCollection = () => "equipment-type-candidates";
var equipmentTypeCandidatePath = (candidateId) => `${equipmentTypeCandidatesCollection()}/${cleanFirestoreId(candidateId)}`;
var equipmentTypeObjectsCollection = (typeId) => `${equipmentTypePath(typeId)}/objects`;
var equipmentTypeRefreshesCollection = (typeId) => `${equipmentTypePath(typeId)}/_refresh`;
var equipmentTypeRefreshPath = (typeId, refreshId) => `${equipmentTypeRefreshesCollection(typeId)}/${cleanFirestoreId(refreshId)}`;
var homesCollection = () => "homes";
var homePath = (homeId) => `${homesCollection()}/${cleanFirestoreId(homeId)}`;
var homeMembersCollection = (homeId) => `${homePath(homeId)}/members`;
var homeMemberPath = (homeId, userId) => `${homeMembersCollection(homeId)}/${cleanFirestoreId(userId)}`;
var homeInvitesCollectionGroupName = () => "invites";
var homeInvitesCollection = (homeId) => `${homePath(homeId)}/${homeInvitesCollectionGroupName()}`;
var homeInvitePath = (homeId, code) => `${homeInvitesCollection(homeId)}/${cleanFirestoreId(code)}`;
var companiesCollection = () => "companies";
var companyPath = (companyId) => `${companiesCollection()}/${cleanFirestoreId(companyId)}`;
var contactsCollectionGroupName = () => "contacts";
var contactsCollection = (homeId) => `${homePath(homeId)}/${contactsCollectionGroupName()}`;
var contactPath = (homeId, contactId) => `${contactsCollection(homeId)}/${cleanFirestoreId(contactId)}`;
var homeSpacesCollection = (homeId) => `${homePath(homeId)}/spaces`;
var homeSpacePath = (homeId, spaceId) => `${homeSpacesCollection(homeId)}/${cleanFirestoreId(spaceId)}`;
var considerationsCollectionGroupName = () => "considerations";
var considerationsCollection = (homeId) => `${homePath(homeId)}/${considerationsCollectionGroupName()}`;
var considerationPath = (homeId, considerationId) => `${considerationsCollection(homeId)}/${cleanFirestoreId(considerationId)}`;
var lessonsCollectionGroupName = () => "lessons";
var lessonsCollection = (homeId) => `${homePath(homeId)}/${lessonsCollectionGroupName()}`;
var lessonPath = (homeId, lessonId) => `${lessonsCollection(homeId)}/${cleanFirestoreId(lessonId)}`;
var taskSubscriptionsCollectionGroupName = () => "task-subscriptions";
var homeTaskSubscriptionsCollection = (homeId) => `${homePath(homeId)}/${taskSubscriptionsCollectionGroupName()}`;
var homeTaskSubscriptionPath = (homeId, subscriptionId) => `${homeTaskSubscriptionsCollection(homeId)}/${cleanFirestoreId(subscriptionId)}`;
var tasksCollectionGroupName = () => "tasks";
var tasksCollection = (homeId) => `${homePath(homeId)}/${tasksCollectionGroupName()}`;
var taskPath = (homeId, taskId) => `${tasksCollection(homeId)}/${cleanFirestoreId(taskId)}`;
var guideStartersCollectionGroupName = () => "guide-starters";
var guideStartersCollection = (homeId) => `${homePath(homeId)}/${guideStartersCollectionGroupName()}`;
var guideStarterPath = (homeId, starterId) => `${guideStartersCollection(homeId)}/${cleanFirestoreId(starterId)}`;
var foldersCollection = (homeId) => `${homePath(homeId)}/folders`;
var folderPath = (homeId, folderId) => `${foldersCollection(homeId)}/${cleanFirestoreId(folderId)}`;
var roomsCollection = (homeId) => `${homePath(homeId)}/rooms`;
var roomPath = (homeId, roomId) => `${roomsCollection(homeId)}/${cleanFirestoreId(roomId)}`;
var roomEnhancementsCollection = (homeId, roomId) => `${roomPath(homeId, roomId)}/enhancements`;
var roomEnhancementPath = (homeId, roomId, enhancementId) => `${roomEnhancementsCollection(homeId, roomId)}/${cleanFirestoreId(enhancementId)}`;
var customEquipmentTypesCollectionGroupName = () => "custom-equipment-types";
var customEquipmentTypesCollection = (homeId) => `${homePath(homeId)}/${customEquipmentTypesCollectionGroupName()}`;
var customEquipmentTypePath = (homeId, typeId) => `${customEquipmentTypesCollection(homeId)}/${cleanFirestoreId(typeId)}`;
var equipmentCollectionGroupName = () => "equipment";
var equipmentCollection = (homeId) => `${homePath(homeId)}/${equipmentCollectionGroupName()}`;
var equipmentPath = (homeId, equipmentId) => `${equipmentCollection(homeId)}/${cleanFirestoreId(equipmentId)}`;
var equipmentRefreshesCollection = (homeId, equipmentId) => `${equipmentPath(homeId, equipmentId)}/_refresh`;
var equipmentRefreshPath = (homeId, equipmentId, refreshId) => `${equipmentRefreshesCollection(homeId, equipmentId)}/${cleanFirestoreId(refreshId)}`;
var planReviewsCollection = (homeId) => `${homePath(homeId)}/plan-reviews`;
var planReviewPath = (homeId, reviewId) => `${planReviewsCollection(homeId)}/${cleanFirestoreId(reviewId)}`;
var homeQuestionsCollection = (homeId) => `${homePath(homeId)}/questions`;
var homeQuestionPath = (homeId, questionId) => `${homeQuestionsCollection(homeId)}/${cleanFirestoreId(questionId)}`;
var systemInfoCollection = (homeId) => `${homePath(homeId)}/system-info`;
var systemInfoPath = (homeId, infoId) => `${systemInfoCollection(homeId)}/${cleanFirestoreId(infoId)}`;
var equipmentLearnRequestsCollection = (homeId, equipmentId) => `${equipmentPath(homeId, equipmentId)}/_learn`;
var equipmentLearnRequestPath = (homeId, equipmentId, learnId) => `${equipmentLearnRequestsCollection(homeId, equipmentId)}/${cleanFirestoreId(learnId)}`;
var guideHelpRequestsCollection = (homeId) => `${homePath(homeId)}/_guide_help`;
var guideHelpRequestPath = (homeId, helpId) => `${guideHelpRequestsCollection(homeId)}/${cleanFirestoreId(helpId)}`;
var homeRecentlyViewedCollection = (homeId) => `${homePath(homeId)}/recently-viewed`;
var homeRecentlyViewedPath = (homeId, recentlyViewedId) => `${homeRecentlyViewedCollection(homeId)}/${cleanFirestoreId(recentlyViewedId)}`;
var userNotificationsCollection = () => "user-notifications";
var userNotificationPath = (userNotificationId) => `${userNotificationsCollection()}/${cleanFirestoreId(userNotificationId)}`;
var userNotificationEmailReceiptsCollection = (userNotificationId) => `${userNotificationPath(userNotificationId)}/email-receipts`;
var userNotificationEmailReceiptPath = (userNotificationId, messageId) => `${userNotificationEmailReceiptsCollection(userNotificationId)}/${cleanFirestoreId(messageId)}`;
var userNotificationPushReceiptsCollection = (userNotificationId) => `${userNotificationPath(userNotificationId)}/push-receipts`;
var userNotificationPushReceiptPath = (userNotificationId, ticketId) => `${userNotificationPushReceiptsCollection(userNotificationId)}/${cleanFirestoreId(ticketId)}`;
var pushReceiptsCollectionGroupName = () => "push-receipts";
var notificationsCollection = (userId) => `${userPath(userId)}/notifications`;
var notificationPath = (userId, notificationId) => `${notificationsCollection(userId)}/${cleanFirestoreId(notificationId)}`;
var notificationConfigCollectionGroupName = () => "notification-config";
var notificationConfigCollection = (userId) => `${userPath(userId)}/${notificationConfigCollectionGroupName()}`;
var notificationConfigPath = (userId, type) => `${notificationConfigCollection(userId)}/${type}`;
var appNotificationConfigPath = () => `${appCollection()}/notification-config`;
var considerationTypesCollection = () => "consideration-types";
var considerationTypePath = (typeId) => `${considerationTypesCollection()}/${cleanFirestoreId(typeId)}`;
var lessonTypesCollection = () => "lesson-types";
var lessonTypePath = (typeId) => `${lessonTypesCollection()}/${cleanFirestoreId(typeId)}`;
var taskTypesCollection = () => "task-types";
var taskTypePath = (typeId) => `${taskTypesCollection()}/${cleanFirestoreId(typeId)}`;
var customTaskTypesCollectionGroupName = () => "custom-task-types";
var customTaskTypesCollection = (homeId) => `${homePath(homeId)}/${customTaskTypesCollectionGroupName()}`;
var customTaskTypePath = (homeId, typeId) => `${customTaskTypesCollection(homeId)}/${cleanFirestoreId(typeId)}`;
var homeEvalResultsCollection = () => "home-eval-results";
var homeEvalResultPath = (resultId) => `${homeEvalResultsCollection()}/${cleanFirestoreId(resultId)}`;
var aiTasksCollection = () => "ai-tasks";
var processHowOftenResultsCollection = () => `${aiTasksCollection()}/task-how-often/ai-how-often-results`;
var processHowOftenResultPath = (resultId) => `${processHowOftenResultsCollection()}/${cleanFirestoreId(resultId)}`;
var appliesToDraftResultsCollection = () => `${aiTasksCollection()}/consideration-applies-to/ai-applies-to-draft-results`;
var appliesToDraftResultPath = (resultId) => `${appliesToDraftResultsCollection()}/${cleanFirestoreId(resultId)}`;
var rockDocsCollection = () => "rock-docs";
var rockDocPath = (docId) => `${rockDocsCollection()}/${cleanFirestoreId(docId)}`;
var considerationDocsCollection = () => "consideration-docs";
var considerationDocPath = (docId) => `${considerationDocsCollection()}/${cleanFirestoreId(docId)}`;
var lessonDocsCollection = () => "lesson-docs";
var lessonDocPath = (docId) => `${lessonDocsCollection()}/${cleanFirestoreId(docId)}`;
var taskDocsCollection = () => "task-docs";
var taskDocPath = (docId) => `${taskDocsCollection()}/${cleanFirestoreId(docId)}`;
var modelDetailsCollection = () => "model-numbers";
var modelsCollection = () => "models";
var modelPath = (modelId) => `${modelsCollection()}/${cleanFirestoreId(modelId)}`;
var modelSpellingsCollection = () => "model-spellings";
var modelSpellingPath = (spellingKey) => `${modelSpellingsCollection()}/${cleanFirestoreId(spellingKey)}`;
var modelDocumentsCollection = () => "documents";
var modelDocumentPath = (docId) => `${modelDocumentsCollection()}/${cleanFirestoreId(docId)}`;
var documentCoverageCollection = () => "document-coverage";
var documentCoveragePath = (key) => `${documentCoverageCollection()}/${cleanFirestoreId(key)}`;
var modelDocumentCandidatesCollectionGroupName = () => "document-candidates";
var modelDocumentCandidatesCollection = (modelId) => `${modelPath(modelId)}/${modelDocumentCandidatesCollectionGroupName()}`;
var modelDocumentCandidatePath = (modelId, candidateId) => `${modelDocumentCandidatesCollection(modelId)}/${cleanFirestoreId(candidateId)}`;
var documentSearchesCollection = (modelId) => `${modelPath(modelId)}/document-searches`;
var documentSearchPath = (modelId, searchId) => `${documentSearchesCollection(modelId)}/${cleanFirestoreId(searchId)}`;
var brandRequestsCollection = () => "brand-requests";
var brandRequestPath = (requestId) => `${brandRequestsCollection()}/${cleanFirestoreId(requestId)}`;
var brandResearchCollection = (requestId) => `${brandRequestPath(requestId)}/research`;
var brandResearchPath = (requestId, researchId) => `${brandResearchCollection(requestId)}/${cleanFirestoreId(researchId)}`;
var brandsCollection = () => "brands";
var brandPath = (brandId) => `${brandsCollection()}/${cleanFirestoreId(brandId)}`;
var brandSourcesCollection = (brandId) => `${brandPath(brandId)}/sources`;
var brandSourcePath = (brandId, sourceId) => `${brandSourcesCollection(brandId)}/${cleanFirestoreId(sourceId)}`;
var brandAliasesCollection = () => "brand-aliases";
var brandAliasPath = (aliasKey) => `${brandAliasesCollection()}/${cleanFirestoreId(aliasKey)}`;
var sharedNameplatesCollection = () => "shared-nameplates";
var sharedNameplatePath = (key) => `${sharedNameplatesCollection()}/${cleanFirestoreId(key)}`;
var brandMergesCollection = () => "brand-merges";
var brandMergePath = (mergeId) => `${brandMergesCollection()}/${cleanFirestoreId(mergeId)}`;
var recallsCollection = () => "recalls";
var recallPath = (recallId) => `${recallsCollection()}/${cleanFirestoreId(recallId)}`;
var recallKeysCollection = () => "recall-keys";
var recallKeyPath = (key) => `${recallKeysCollection()}/${cleanFirestoreId(key)}`;
var recallMatchesCollectionGroupName = () => "recall-matches";
var recallMatchesCollection = (recallId) => `${recallPath(recallId)}/${recallMatchesCollectionGroupName()}`;
var recallMatchPath = (recallId, modelId) => `${recallMatchesCollection(recallId)}/${cleanFirestoreId(modelId)}`;
var recallVerdictsCollectionGroupName = () => "recall-verdicts";
var recallVerdictsCollection = (homeId) => `${homePath(homeId)}/${recallVerdictsCollectionGroupName()}`;
var recallVerdictPath = (key) => `${recallVerdictsCollection(key.homeId)}/${cleanFirestoreId(recallVerdictId(key))}`;
var legacyRecallVerdictPath = (key) => `${equipmentPath(key.homeId, key.equipmentId)}/${recallVerdictsCollectionGroupName()}/${cleanFirestoreId(key.recallId)}`;
var appRecallsStatePath = () => `${appCollection()}/${RECALLS_STATE_ID}`;
var recallTopicsCollection = () => "recall-topics";
var recallTopicPath = (topicId) => `${recallTopicsCollection()}/${cleanFirestoreId(topicId)}`;
var recallJobsCollection = () => "recall-jobs";
var recallJobPath = (jobId) => `${recallJobsCollection()}/${cleanFirestoreId(jobId)}`;
var usersCollection = () => "users";
var userPath = (userId) => `${usersCollection()}/${cleanFirestoreId(userId)}`;
var userAuthEventsCollection = (userId) => `${userPath(userId)}/auth-events`;
var userRolesCollection = () => "user-roles";
var userRolesPath = (userId) => `${userRolesCollection()}/${cleanFirestoreId(userId)}`;
var userLimitsCollection = () => "user-limits";
var userLimitsPath = (userId) => `${userLimitsCollection()}/${cleanFirestoreId(userId)}`;
var userDevicesCollection = (userId) => `${userPath(userId)}/devices`;
var userDevicePath = (userId, deviceId) => `${userDevicesCollection(userId)}/${cleanFirestoreId(deviceId)}`;
var devicesCollectionGroupName = () => "devices";
var chatConversationsCollection = (userId) => `${userPath(userId)}/chat-conversations`;
var chatConversationPath = (userId, conversationId) => `${chatConversationsCollection(userId)}/${cleanFirestoreId(conversationId)}`;
var userTaskScreenConfigCollection = (userId) => `${userPath(userId)}/task-screen-config`;
var userTaskScreenConfigPath = (userId, taskTypeId) => `${userTaskScreenConfigCollection(userId)}/${cleanFirestoreId(taskTypeId)}`;
var aiHelpCollection = () => "ai-help";
var learnCacheCollection = () => `${aiHelpCollection()}/learn-more/ai-learn-more-entries`;
var learnCacheEntryPath = (key) => `${learnCacheCollection()}/${cleanFirestoreId(key)}`;
var guideHelpCacheCollection = () => `${aiHelpCollection()}/guide-screen/ai-guide-screen-entries`;
var guideHelpCacheEntryPath = (key) => `${guideHelpCacheCollection()}/${cleanFirestoreId(key)}`;
var equipmentImageSearchRequestsCollection = (homeId) => `${homePath(homeId)}/_equipment_image_search`;
var equipmentImageSearchRequestPath = (homeId, requestId) => `${equipmentImageSearchRequestsCollection(homeId)}/${cleanFirestoreId(requestId)}`;
var modelNumberHelpRequestsCollection = (homeId) => `${homePath(homeId)}/_model_number_help`;
var modelNumberHelpRequestPath = (homeId, helpId) => `${modelNumberHelpRequestsCollection(homeId)}/${cleanFirestoreId(helpId)}`;
var modelNumberHelpCacheCollection = () => `${aiHelpCollection()}/model-number/ai-model-number-entries`;
var modelNumberHelpCacheEntryPath = (key) => `${modelNumberHelpCacheCollection()}/${cleanFirestoreId(key)}`;
var propertyLookupRequestsCollection = (homeId) => `${homePath(homeId)}/_property_lookup`;
var propertyLookupRequestPath = (homeId, requestId) => `${propertyLookupRequestsCollection(homeId)}/${cleanFirestoreId(requestId)}`;
var domainEventsCollection = () => "domain-events";
var domainEventPath = (eventId) => `${domainEventsCollection()}/${cleanFirestoreId(eventId)}`;
var notionWebhookEventsCollection = () => "notion-webhook-events";
var notionWebhookEventPath = (eventId) => `${notionWebhookEventsCollection()}/${cleanFirestoreId(eventId)}`;
var jobsCollection = () => "jobs";
var homeEvalJobRunsCollection = () => `${jobsCollection()}/home-eval/job-runs`;
var homeEvalJobRunPath = (jobId) => `${homeEvalJobRunsCollection()}/${cleanFirestoreId(jobId)}`;
var taskGenerationJobRunsCollection = () => `${jobsCollection()}/task-generation/job-runs`;
var taskGenerationJobRunPath = (runId) => `${taskGenerationJobRunsCollection()}/${cleanFirestoreId(runId)}`;
var rockManualsJobRunsCollection = () => `${jobsCollection()}/rock-manuals/job-runs`;
var rockManualsJobRunPath = (jobId) => `${rockManualsJobRunsCollection()}/${cleanFirestoreId(jobId)}`;
var homeEvalTriggersCollection = () => "home-eval-triggers";
var homeEvalTriggerPath = (homeId) => `${homeEvalTriggersCollection()}/${cleanFirestoreId(homeId)}`;
var weekendFillsCollection = () => "weekend-fills";
var weekendFillPath = (homeId) => `${weekendFillsCollection()}/${cleanFirestoreId(homeId)}`;
var spaceFlowPath = (spaceId, typeId) => spaceId === DEFAULT_EQUIPMENT_SPACE_ID ? `${appEquipmentConfigObjectsCollection()}/flow` : typeId ? `${equipmentTypeObjectsCollection(typeId)}/flow` : `${spaceObjectsCollection(spaceId)}/flow`;
var draftFlowsCollection = (spaceId) => spaceId === DEFAULT_EQUIPMENT_SPACE_ID ? `${appEquipmentConfigPath()}/drafts` : `${spacePath(spaceId)}/drafts`;
var draftFlowPath = (spaceId, flowId) => `${draftFlowsCollection(spaceId)}/${flowId}`;

export {
  FirestoreDocPathSchema,
  DRAFT_ROOT,
  toDraftPath,
  fromDraftPath,
  appCollection,
  appEquipmentConfigPath,
  appTaskScreenConfigPath,
  appUserLimitsConfigPath,
  appBrandSearchIndexPath,
  appEquipmentConfigObjectsCollection,
  appDefaultEquipmentGuidePath,
  appStatusCollection,
  appStatusDocPath,
  systemCollection,
  resetStatePath,
  feedbackCollection,
  feedbackEntryPath,
  spacesCollection,
  spacePath,
  spaceObjectsCollection,
  guidePath,
  guideVisitsCollection,
  guideVisitPath,
  equipmentTypesCollection,
  equipmentTypePath,
  equipmentTypeFamiliesCollection,
  equipmentTypeFamilyPath,
  infoTypesCollection,
  infoTypePath,
  equipmentTestLibraryCollection,
  equipmentTestLibraryEntryPath,
  equipmentTypeCandidatesCollection,
  equipmentTypeCandidatePath,
  equipmentTypeObjectsCollection,
  equipmentTypeRefreshesCollection,
  equipmentTypeRefreshPath,
  homesCollection,
  homePath,
  homeMembersCollection,
  homeMemberPath,
  homeInvitesCollectionGroupName,
  homeInvitesCollection,
  homeInvitePath,
  companiesCollection,
  companyPath,
  contactsCollectionGroupName,
  contactsCollection,
  contactPath,
  homeSpacesCollection,
  homeSpacePath,
  considerationsCollectionGroupName,
  considerationsCollection,
  considerationPath,
  lessonsCollectionGroupName,
  lessonsCollection,
  lessonPath,
  taskSubscriptionsCollectionGroupName,
  homeTaskSubscriptionsCollection,
  homeTaskSubscriptionPath,
  tasksCollectionGroupName,
  tasksCollection,
  taskPath,
  guideStartersCollectionGroupName,
  guideStartersCollection,
  guideStarterPath,
  foldersCollection,
  folderPath,
  roomsCollection,
  roomPath,
  roomEnhancementsCollection,
  roomEnhancementPath,
  customEquipmentTypesCollectionGroupName,
  customEquipmentTypesCollection,
  customEquipmentTypePath,
  equipmentCollectionGroupName,
  equipmentCollection,
  equipmentPath,
  equipmentRefreshesCollection,
  equipmentRefreshPath,
  planReviewsCollection,
  planReviewPath,
  homeQuestionsCollection,
  homeQuestionPath,
  systemInfoCollection,
  systemInfoPath,
  equipmentLearnRequestsCollection,
  equipmentLearnRequestPath,
  guideHelpRequestsCollection,
  guideHelpRequestPath,
  homeRecentlyViewedCollection,
  homeRecentlyViewedPath,
  userNotificationsCollection,
  userNotificationPath,
  userNotificationEmailReceiptsCollection,
  userNotificationEmailReceiptPath,
  userNotificationPushReceiptsCollection,
  userNotificationPushReceiptPath,
  pushReceiptsCollectionGroupName,
  notificationsCollection,
  notificationPath,
  notificationConfigCollectionGroupName,
  notificationConfigCollection,
  notificationConfigPath,
  appNotificationConfigPath,
  considerationTypesCollection,
  considerationTypePath,
  lessonTypesCollection,
  lessonTypePath,
  taskTypesCollection,
  taskTypePath,
  customTaskTypesCollectionGroupName,
  customTaskTypesCollection,
  customTaskTypePath,
  homeEvalResultsCollection,
  homeEvalResultPath,
  aiTasksCollection,
  processHowOftenResultsCollection,
  processHowOftenResultPath,
  appliesToDraftResultsCollection,
  appliesToDraftResultPath,
  rockDocsCollection,
  rockDocPath,
  considerationDocsCollection,
  considerationDocPath,
  lessonDocsCollection,
  lessonDocPath,
  taskDocsCollection,
  taskDocPath,
  modelDetailsCollection,
  modelsCollection,
  modelPath,
  modelSpellingsCollection,
  modelSpellingPath,
  modelDocumentsCollection,
  modelDocumentPath,
  documentCoverageCollection,
  documentCoveragePath,
  modelDocumentCandidatesCollectionGroupName,
  modelDocumentCandidatesCollection,
  modelDocumentCandidatePath,
  documentSearchesCollection,
  documentSearchPath,
  brandRequestsCollection,
  brandRequestPath,
  brandResearchCollection,
  brandResearchPath,
  brandsCollection,
  brandPath,
  brandSourcesCollection,
  brandSourcePath,
  brandAliasesCollection,
  brandAliasPath,
  sharedNameplatesCollection,
  sharedNameplatePath,
  brandMergesCollection,
  brandMergePath,
  recallsCollection,
  recallPath,
  recallKeysCollection,
  recallKeyPath,
  recallMatchesCollectionGroupName,
  recallMatchesCollection,
  recallMatchPath,
  recallVerdictsCollectionGroupName,
  recallVerdictsCollection,
  recallVerdictPath,
  legacyRecallVerdictPath,
  appRecallsStatePath,
  recallTopicsCollection,
  recallTopicPath,
  recallJobsCollection,
  recallJobPath,
  usersCollection,
  userPath,
  userAuthEventsCollection,
  userRolesCollection,
  userRolesPath,
  userLimitsCollection,
  userLimitsPath,
  userDevicesCollection,
  userDevicePath,
  devicesCollectionGroupName,
  chatConversationsCollection,
  chatConversationPath,
  userTaskScreenConfigCollection,
  userTaskScreenConfigPath,
  aiHelpCollection,
  learnCacheCollection,
  learnCacheEntryPath,
  guideHelpCacheCollection,
  guideHelpCacheEntryPath,
  equipmentImageSearchRequestsCollection,
  equipmentImageSearchRequestPath,
  modelNumberHelpRequestsCollection,
  modelNumberHelpRequestPath,
  modelNumberHelpCacheCollection,
  modelNumberHelpCacheEntryPath,
  propertyLookupRequestsCollection,
  propertyLookupRequestPath,
  domainEventsCollection,
  domainEventPath,
  notionWebhookEventsCollection,
  notionWebhookEventPath,
  jobsCollection,
  homeEvalJobRunsCollection,
  homeEvalJobRunPath,
  taskGenerationJobRunsCollection,
  taskGenerationJobRunPath,
  rockManualsJobRunsCollection,
  rockManualsJobRunPath,
  homeEvalTriggersCollection,
  homeEvalTriggerPath,
  weekendFillsCollection,
  weekendFillPath,
  spaceFlowPath,
  draftFlowsCollection,
  draftFlowPath
};
//# sourceMappingURL=chunk-QM34SWEU.js.map